import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  APPROVAL_STATUS,
  NODE_TYPE,
  WORKFLOW_STATUS,
  getWorkflowInstance,
  getWorkflowInstances,
} from '../../services/workflowService';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { approvalRequestI18n } from './i18n';
import ApprovalFlowModal from './ApprovalFlowModal';
import './approvalRequest.scss';

const getPriorityBadgeColor = (priority) => {
  if (priority === 'high') return 'error';
  if (priority === 'medium') return 'warning';
  if (priority === 'low') return 'info';
  return 'light';
};

const getStatusBadgeColor = (status) => {
  if (status === 'pending') return 'warning';
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'error';
  return 'light';
};

const parseEnumNumber = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return null;

  const normalized = value.trim().toLowerCase();
  if (normalized === 'task') return NODE_TYPE.TASK;
  if (normalized === 'approval') return NODE_TYPE.APPROVAL;
  if (normalized === 'pending') return APPROVAL_STATUS.PENDING;
  if (normalized === 'approved') return APPROVAL_STATUS.APPROVED;
  if (normalized === 'rejected') return APPROVAL_STATUS.REJECTED;
  if (normalized === 'inprogress') return WORKFLOW_STATUS.IN_PROGRESS;
  if (normalized === 'approvedworkflow') return WORKFLOW_STATUS.APPROVED;
  if (normalized === 'finalized') return WORKFLOW_STATUS.FINALIZED;

  const asNumber = Number(value);
  return Number.isFinite(asNumber) ? asNumber : null;
};

const formatDate = (isoDate) => {
  if (!isoDate) return '-';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '-';
  return `${String(date.getDate()).padStart(2, '0')}/${String(
    date.getMonth() + 1
  ).padStart(2, '0')}/${date.getFullYear()}`;
};

const resolveRequestStatus = (workflowStatus, approvalNodes) => {
  const workflowStatusValue = parseEnumNumber(workflowStatus);
  const hasRejectedApproval = approvalNodes.some(
    (node) => parseEnumNumber(node.approvalStatus) === APPROVAL_STATUS.REJECTED
  );

  if (workflowStatusValue === WORKFLOW_STATUS.REJECTED || hasRejectedApproval) {
    return 'rejected';
  }

  const totals = approvalNodes.reduce(
    (acc, node) => {
      const completedRaw =
        node?.approvalCompletedCount ??
        node?.approvalcompletedcount ??
        null;
      const totalRaw =
        node?.approvalTotalCount ??
        node?.approvaltotalcount ??
        null;

      const completed = Number(completedRaw);
      const total = Number(totalRaw);

      if (Number.isFinite(total) && total > 0) {
        acc.total += total;
        acc.completed += Number.isFinite(completed) ? completed : 0;
        return acc;
      }

      const approvalStatus = parseEnumNumber(node?.approvalStatus);
      const nodeStatus = parseEnumNumber(node?.status);
      acc.total += 1;
      if (
        approvalStatus === APPROVAL_STATUS.APPROVED ||
        nodeStatus === 3
      ) {
        acc.completed += 1;
      }
      return acc;
    },
    { completed: 0, total: 0 }
  );

  const approvedSteps = totals.completed;
  const totalApprovalDecisions = totals.total;

  if (
    totalApprovalDecisions > 0 &&
    approvedSteps === totalApprovalDecisions &&
    (workflowStatusValue === WORKFLOW_STATUS.APPROVED ||
      workflowStatusValue === WORKFLOW_STATUS.FINALIZED)
  ) {
    return 'approved';
  }

  return 'pending';
};

const mapInstanceToRequest = (instance, t) => {
  const nodes = Array.isArray(instance.nodes) ? instance.nodes : [];
  const approvalNodes = nodes.filter(
    (node) => parseEnumNumber(node.nodeType) === NODE_TYPE.APPROVAL
  );

  const progress = approvalNodes.reduce(
    (acc, node) => {
      const completedRaw =
        node?.approvalCompletedCount ??
        node?.approvalcompletedcount ??
        null;
      const totalRaw =
        node?.approvalTotalCount ??
        node?.approvaltotalcount ??
        null;

      const completed = Number(completedRaw);
      const total = Number(totalRaw);
      if (Number.isFinite(total) && total > 0) {
        acc.total += total;
        acc.completed += Number.isFinite(completed) ? completed : 0;
        return acc;
      }

      const approvalStatus = parseEnumNumber(node?.approvalStatus);
      const nodeStatus = parseEnumNumber(node?.status);
      acc.total += 1;
      if (
        approvalStatus === APPROVAL_STATUS.APPROVED ||
        nodeStatus === 3
      ) {
        acc.completed += 1;
      }
      return acc;
    },
    { completed: 0, total: 0 }
  );

  const approvedSteps = progress.completed;
  const totalSteps = progress.total;
  const status = resolveRequestStatus(instance.status, approvalNodes);

  const taskName = instance.referenceCode
    ? `${instance.workflowName} - ${instance.referenceCode}`
    : instance.workflowName;

  return {
    id: instance.id,
    instanceId: instance.id,
    task: taskName || t('ar_fallback_task'),
    priority: 'medium',
    requester:
      instance.initiatedByName ||
      (instance.initiatedByUserId
        ? t('ar_fallback_requester_id', { id: instance.initiatedByUserId })
        : t('ar_fallback_requester')),
    date: formatDate(instance.startedAtUtc),
    workflow: instance.workflowName || '-',
    approvedSteps,
    totalSteps,
    status,
  };
};

const ApprovalRequest = ({ DesignSystemComponents }) => {
  const { Card, Table, Dropdown, Input, SvgIcon, Badge } =
    DesignSystemComponents;

  const { language } = useLanguage();
  const arLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: approvalRequestI18n });

  useEffect(() => {
    approvalRequestI18n.changeLanguage(arLanguage);
  }, [arLanguage]);

  const statusOptions = useMemo(
    () => [
      { label: t('ar_filter_all'), value: 'all' },
      { label: t('ar_filter_pending'), value: 'pending' },
      { label: t('ar_filter_approved'), value: 'approved' },
      { label: t('ar_filter_rejected'), value: 'rejected' },
    ],
    [t]
  );

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [flowLoading, setFlowLoading] = useState(false);

  const effectiveSelectedStatus = selectedStatus ?? statusOptions[0];

  const loadInstances = async () => {
    setLoading(true);
    setError('');

    try {
      const instances = await getWorkflowInstances();
      setRequests(
        instances.map((instance) => mapInstanceToRequest(instance, t))
      );
    } catch (serviceError) {
      setError(
        serviceError?.customMessage ||
          serviceError?.message ||
          t('ar_error_load')
      );
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInstances();
  }, []);

  const stats = useMemo(() => {
    return {
      total: requests.length,
      pending: requests.filter((item) => item.status === 'pending').length,
      approved: requests.filter((item) => item.status === 'approved').length,
      rejected: requests.filter((item) => item.status === 'rejected').length,
    };
  }, [requests]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return requests.filter((item) => {
      const matchesStatus =
        effectiveSelectedStatus.value === 'all' ||
        item.status === effectiveSelectedStatus.value;

      if (!matchesStatus) return false;

      if (!normalizedSearch) return true;

      const searchable =
        `${item.task} ${item.requester} ${item.workflow}`.toLowerCase();
      return searchable.includes(normalizedSearch);
    });
  }, [requests, searchTerm, effectiveSelectedStatus]);

  const handleViewFlow = async (requestRow) => {
    setFlowLoading(true);
    setError('');

    try {
      const detail = await getWorkflowInstance(requestRow.instanceId);
      setSelectedFlow(detail);
      setSelectedRequest(requestRow);
    } catch (serviceError) {
      setError(
        serviceError?.customMessage ||
          serviceError?.message ||
          t('ar_error_load_flow')
      );
      setSelectedFlow(null);
      setSelectedRequest(null);
    } finally {
      setFlowLoading(false);
    }
  };

  const resolveStatusLabel = (status) => {
    if (status === 'pending') return t('ar_status_pending');
    if (status === 'approved') return t('ar_status_approved');
    if (status === 'rejected') return t('ar_status_rejected');
    return status;
  };

  const resolvePriorityLabel = (priority) => {
    if (priority === 'high') return t('ar_priority_high');
    if (priority === 'medium') return t('ar_priority_medium');
    if (priority === 'low') return t('ar_priority_low');
    return priority;
  };

  return (
    <div className='approval-request-page'>
      <div className='mb-4'>
        <h2 className='fw-semibold mb-1'>{t('ar_page_title')}</h2>
        <h4 className='fw-normal'>{t('ar_page_subtitle')}</h4>
      </div>

      {error && <p className='color-error-500 mb-3'>{error}</p>}

      <div className='row g-3 mb-4'>
        <div className='col-12 col-md-3'>
          <Card specialPadding='p-3'>
            <p className='caption font-light mb-1'>{t('ar_kpi_total')}</p>
            <h2 className='fw-semibold mb-0'>{stats.total}</h2>
          </Card>
        </div>
        <div className='col-12 col-md-3'>
          <Card specialPadding='p-3'>
            <p className='caption font-light mb-1'>{t('ar_kpi_pending')}</p>
            <h2 className='fw-semibold mb-0 color-warning-500'>
              {stats.pending}
            </h2>
          </Card>
        </div>
        <div className='col-12 col-md-3'>
          <Card specialPadding='p-3'>
            <p className='caption font-light mb-1'>{t('ar_kpi_approved')}</p>
            <h2 className='fw-semibold mb-0 color-success-500'>
              {stats.approved}
            </h2>
          </Card>
        </div>
        <div className='col-12 col-md-3'>
          <Card specialPadding='p-3'>
            <p className='caption font-light mb-1'>{t('ar_kpi_rejected')}</p>
            <h2 className='fw-semibold mb-0 color-error-500'>
              {stats.rejected}
            </h2>
          </Card>
        </div>
      </div>

      <Card specialPadding='p-3'>
        <div className='d-flex flex-wrap justify-content-between align-items-center'>
          <h3 className='fw-semibold space-like-input'>
            {t('ar_table_title')}
          </h3>
          <div className='approval-filters'>
            <div className='approval-filter-select'>
              <Dropdown
                value={effectiveSelectedStatus}
                onChange={setSelectedStatus}
                options={statusOptions}
                className='space-like-input'
              />
            </div>
            <div className='approval-filter-input'>
              <Input
                icon='search'
                placeholder={t('ar_search_placeholder')}
                type='text'
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
          </div>
        </div>

        <div className='table-container table-approval-request'>
          <Table
            columns={[
              {
                accessor: 'task',
                label: t('ar_col_task'),
                render: (row) => (
                  <div>
                    <p className='caption mb-1'>{row.task}</p>
                    <Badge
                      text={resolvePriorityLabel(row.priority)}
                      color={getPriorityBadgeColor(row.priority)}
                      size='sm'
                    />
                  </div>
                ),
              },
              { accessor: 'requester', label: t('ar_col_requester') },
              { accessor: 'date', label: t('ar_col_date') },
              {
                accessor: 'workflow',
                label: t('ar_col_workflow'),
                render: (row) => (
                  <div className='d-flex align-items-center gap-2'>
                    <SvgIcon
                      name='listTask'
                      size='14px'
                      stroke='var(--primary-500)'
                    />
                    <span className='caption'>{row.workflow}</span>
                  </div>
                ),
              },
              {
                accessor: 'progress',
                label: t('ar_col_progress'),
                render: (row) => {
                  const progress =
                    row.totalSteps > 0
                      ? (row.approvedSteps / row.totalSteps) * 100
                      : 0;

                  return (
                    <div className='approval-progress'>
                      <div className='approval-progress__track'>
                        <div
                          className={`approval-progress__fill ${
                            row.status === 'rejected'
                              ? 'approval-progress__fill--rejected'
                              : ''
                          }`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <span className='mini font-light'>
                        {row.approvedSteps}/{row.totalSteps}
                      </span>
                    </div>
                  );
                },
              },
              {
                accessor: 'status',
                label: t('ar_col_status'),
                render: (row) => (
                  <Badge
                    text={resolveStatusLabel(row.status)}
                    color={getStatusBadgeColor(row.status)}
                    size='sm'
                  />
                ),
              },
              {
                label: t('ar_col_actions'),
                render: (row) => (
                  <button
                    type='button'
                    className='approval-link-btn'
                    onClick={() => handleViewFlow(row)}
                    disabled={flowLoading}
                  >
                    <SvgIcon
                      name='eye'
                      size='14px'
                      stroke='var(--primary-500)'
                    />
                    {t('ar_btn_view_flow')}
                  </button>
                ),
              },
            ]}
            data={filteredRows}
            loading={loading}
          />
        </div>
      </Card>

      <ApprovalFlowModal
        DesignSystemComponents={DesignSystemComponents}
        show={!!selectedFlow}
        flow={selectedFlow}
        request={selectedRequest}
        onClose={() => {
          setSelectedFlow(null);
          setSelectedRequest(null);
        }}
      />
    </div>
  );
};

export default ApprovalRequest;
